import React, { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { FaChevronDown, FaTrashAlt } from "react-icons/fa";
import { HiArrowDownTray } from "react-icons/hi2";
import { BiUpload } from "react-icons/bi";
import axiosConfig from "../../Service/AxiosConfig";
import DataTable from "react-data-table-component";
import { PopupModal } from "../../PopupModal/PopupModal";
import ShowDeleteModal from "../../Modal/ShowDeleteModal";

const Documents = ({ form, setForm, fetchBooking }) => {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [popupType, setPopupType] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [newDocs, setNewDocs] = useState([]);
  const fileRefs = useRef({});
  const documentTypes = [
    { label: "Previous Employer", value: "employer_file" },
    { label: "Police Verification", value: "police_file" },
    { label: "Resume (Optional)", value: "resume_file" },
  ];

  const allowedTypes = documentTypes.map((doc) => doc.value);

  const documents = (form?.documents || []).filter(
    (doc) => doc?.document_type && allowedTypes.includes(doc.document_type),
  );
  const hasBackendDocs = documents.length > 0;
  const hasNewDocs = newDocs.length > 0;
  const isDocumentUploaded = (type) => {
    return (
      documents.some((doc) => doc.document_type === type) ||
      newDocs.some((doc) => doc.type === type)
    );
  };

  useEffect(() => {
    if (!form?.id) return;
    if (documents.length === 0) {
      setEditMode(true);
    } else {
      setEditMode(false);
    }

    setIsDirty(false);
  }, [form?.id, documents.length]);
  const handleFileChange = (file, type) => {
    if (!file) return;

    const MAX_FILE_SIZE = 2 * 1024 * 1024;
    const validTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setPopupType("error");
      setError("Only PDF, JPG, PNG allowed");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setPopupType("error");
      setError("Max file size is 2MB");
      return;
    }
    setIsDirty(true);
    if (isDocumentUploaded(type)) {
      setPopupType("error");
      setError("This document already exists");
      return;
    }
    setIsDirty(true);

    setNewDocs((prev) => [...prev, { file, type }]);
  };

  // ================= SAVE =================
  const handleSave = async () => {
    const payload = new FormData();

    newDocs.forEach((doc, index) => {
      payload.append(`documents[${index}].file`, doc.file);
      payload.append(`documents[${index}].type`, doc.type);
    });

    try {
      setSaving(true);

      await axiosConfig.patch(`/provider-data/providers/${form.id}/`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setForm((prev) => ({
        ...prev,
        documents: [
          ...(prev.documents || []),
          ...newDocs.map((doc, index) => ({
            id: `temp-${Date.now()}-${index}`,
            document_type: doc.type,
            document_url: URL.createObjectURL(doc.file),
            is_verified: false,
          })),
        ],
      }));

      setNewDocs([]);
      setEditMode(false);
      setExpanded(false);
      setIsDirty(false);
      setPopupType("success");
      setMessage("Documents saved successfully");
      fetchBooking();
    } catch {
      setPopupType("error");
      setError("Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      if (String(deleteId).startsWith("new-")) {
        const index = Number(deleteId.split("-")[1]);
        setNewDocs((prev) => prev.filter((_, i) => i !== index));
      } else {
        await axiosConfig.delete(`/provider-data/documents/${deleteId}/`);

        setForm((prev) => ({
          ...prev,
          documents: prev.documents.filter((doc) => doc.id !== deleteId),
        }));
      }

      setPopupType("success");
      setMessage("Document deleted successfully");
      setIsDirty(true);
      fetchBooking();
    } catch {
      setPopupType("error");
      setError("Delete failed");
    } finally {
      setShowDeletePopup(false);
    }
  };

  // ================= TABLE =================
  const columns = [
    {
      name: "S.No",
      selector: (_, index) => index + 1,
      width: "80px",
    },
    {
      name: "File Name",
      selector: (row) => row.document_type,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          style={{
            color: row.is_verified ? "green" : "red",
            fontWeight: 500,
          }}
        >
          {row.is_verified ? "Verified" : "Not Verified"}
        </span>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", gap: 12 }}>
          <button
            disabled={!editMode}
            onClick={() => {
              if (!editMode) return;
              setDeleteId(row.id);
              setShowDeletePopup(true);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: editMode ? "pointer" : "not-allowed",
            }}
          >
            <FaTrashAlt size={14} color={editMode ? "red" : "#ccc"} />
          </button>

          <a href={row.document_url} target="_blank" rel="noreferrer">
            <HiArrowDownTray size={18} />
          </a>
        </div>
      ),
    },
  ];

  const tableData = [
    ...documents,
    ...newDocs.map((doc, index) => ({
      id: `new-${index}`,
      document_type: doc.type,
      document_url: URL.createObjectURL(doc.file),
      is_verified: false,
    })),
  ];

  return (
    <>
      <Accordion
        key={expanded ? "open" : "closed"}
        allowZeroExpanded
        preExpanded={expanded ? ["documents"] : []}
        onChange={(uuids) => {
          setExpanded(uuids.includes("documents"));
        }}
      >
        <AccordionItem uuid="documents">
          <AccordionItemHeading>
            <AccordionItemButton className="bd-header">
              <h4 className="bd-title">Documents</h4>
              <span className="bd-add-btn">
                Add Details
                <FaChevronDown
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </span>
            </AccordionItemButton>
          </AccordionItemHeading>

          <AccordionItemPanel className="bd-panel">
            <div className="bd-section-grid bd-grid-3 service-cards">
              {documentTypes.map((doc) => {
                const uploaded = isDocumentUploaded(doc.value);

                return (
                  <div className="form-group" key={doc.value}>
                    <label
                      className={`form-label ${!editMode ? "view-mode-label" : ""}`}
                    >
                      {doc.label}
                    </label>

                    <div
                      className="form-group icon-upload"
                      style={{
                        opacity: uploaded ? 0.5 : 1,
                        cursor:
                          uploaded || !editMode ? "not-allowed" : "pointer",
                      }}
                      onClick={() => {
                        if (!editMode || uploaded) return;
                        fileRefs.current[doc.value]?.click();
                      }}
                    >
                      <BiUpload size={18} />
                      <span>{uploaded ? "Uploaded" : "Upload"}</span>

                      <input
                        type="file"
                        hidden
                        ref={(el) => (fileRefs.current[doc.value] = el)}
                        disabled={!editMode || uploaded}
                        onChange={(e) =>
                          handleFileChange(e.target.files[0], doc.value)
                        }
                      />
                    </div>
                  </div>
                );
              })}

              <div className="form-group full-width">
                <DataTable
                  columns={columns}
                  data={tableData}
                  dense
                  highlightOnHover
                  progressPending={saving}
                />
              </div>
              <div className="form-group full-width">
                <div className="bd-actions">
                  {!editMode ? (
                    <button
                      className="bd-edit-btn"
                      onClick={() => {
                        setEditMode(true);
                        setIsDirty(true);
                        setExpanded(true);
                      }}
                    >
                      Edit
                    </button>
                  ) : (
                    isDirty && (
                      <button
                        className="bd-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        Save
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>

      {showDeletePopup && (
        <ShowDeleteModal
          open={showDeletePopup}
          onConfirm={confirmDelete}
          onCancel={() => setShowDeletePopup(false)}
        />
      )}

      {popupType && (
        <PopupModal
          setpopupType={setPopupType}
          popupType={popupType}
          error={error}
          Message={message}
          setMessage={setMessage}
        />
      )}
    </>
  );
};

export default Documents;
